import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { getVendors } from '../../api/master'
import { listVendorOpenInvoices, saveAPPayment } from '../../api/purchase'
import { getApAging } from '../../api/dashboard'
import ErrorBanner from '../../components/ErrorBanner'
import DefaultAccountSelect from '../../components/DefaultAccountSelect'
import { useToast } from '../../components/Toast.jsx'
import { BackLink } from '../../components/PageParts'
import { fmtDay, localToday, parseWallClock, startOfToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { useSession } from '../../session'

// Paying vendors: what each one is owed, and recording a payment against
// their bills. Serves both the AP ledger and the payment routes, under
// Purchase (with tabs) and under Accounting.
export default function APPayment() {
  const { has } = useSession()
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const [sp] = useSearchParams()
  const location = useLocation()
  const viaAccounting = location.pathname.startsWith('/accounting/')
  const focusInvoice = sp.get('invoice_id') || ''
  const { notify } = useToast()

  const [vendors, setVendors] = useState([])
  const [owing, setOwing] = useState({}) // vendor_id -> balance
  const [vendorId, setVendorId] = useState(sp.get('vendor_id') || '')
  const [rows, setRows] = useState([])
  const [date, setDate] = useState(localToday())
  const [method, setMethod] = useState('Transfer')
  const [payFrom, setPayFrom] = useState('')
  const [ref, setRef] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const loadOwing = async () => {
    const ap = await getApAging({ as_of: localToday(), limit: 500 }).catch(() => null)
    const totals = Object.fromEntries((ap?.vendors || []).map((v) => [String(v.vendor_id), num(v.balance)]))
    setOwing(totals)
    return totals
  }

  const loadVendor = async (vid, focus = '') => {
    if (!vid) { setRows([]); return }
    setLoading(true)
    setError('')
    try {
      const data = await listVendorOpenInvoices(vid)
      setRows((data || []).map((d) => {
        const balance = num(d.balance)
        return { ...d, balance, pay: focus && String(d.invoice_id) === String(focus) ? balance.toFixed(2) : '' }
      }))
    } catch (e) {
      setRows([])
      setError(e.message || 'Could not load this vendor’s bills.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    Promise.all([getVendors().catch(() => []), loadOwing()]).then(([vs, totals]) => {
      if (cancelled) return
      setVendors(Array.isArray(vs) ? vs : [])
      // Start with the vendor from the link, or else whoever is owed the most.
      const first = sp.get('vendor_id') || Object.entries(totals).sort((a, b) => b[1] - a[1])[0]?.[0] || ''
      setVendorId(String(first))
      loadVendor(first, focusInvoice)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chooseVendor = (id) => {
    setVendorId(id)
    loadVendor(id)
  }

  const [owed, others] = useMemo(() => {
    const sorted = [...vendors].sort((a, b) => String(a.name).localeCompare(String(b.name)))
    return [sorted.filter((v) => owing[String(v.vendor_id)] > 0), sorted.filter((v) => !(owing[String(v.vendor_id)] > 0))]
  }, [vendors, owing])

  const selected = vendors.find((v) => String(v.vendor_id) === String(vendorId))
  const totalPay = rows.reduce((s, r) => s + num(r.pay), 0)
  const today = startOfToday()
  const setRowPay = (idx, val) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, pay: val } : r)))

  const onSave = async () => {
    const lines = rows.map((r) => ({ invoice_id: r.invoice_id, amount: num(r.pay), balance: r.balance, number: r.invoice_number })).filter((x) => x.amount > 0)
    if (!vendorId) { setError('Choose the vendor you paid.'); return }
    if (!date) { setError('Enter the date you paid.'); return }
    if (!lines.length) { setError('Enter how much you paid against at least one bill.'); return }
    const over = lines.find((l) => l.amount - l.balance > 0.004)
    if (over) { setError(`That is more than is owed on bill ${over.number || over.invoice_id}.`); return }
    setSaving(true)
    setError('')
    try {
      await saveAPPayment({ vendor_id: parseInt(vendorId, 10), payment_date: date, method, pay_from_account_code: payFrom || undefined, reference_no: ref, lines: lines.map(({ invoice_id, amount }) => ({ invoice_id, amount })) })
      notify(`Payment of ${money(totalPay)} saved`)
      setRef('')
      await Promise.all([loadVendor(vendorId), loadOwing()])
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || 'Could not save the payment.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ar-pay-page">
      {viaAccounting ? <BackLink to="/accounting" label="Accounting" /> : <PurchaseTabs />}
      <div className="ar-pay-header">
        <div className="ar-pay-title-block">
          <div>
            <h1>Pay vendors</h1>
            <div className="ar-pay-subtitle">What you owe each vendor, and recording what you’ve paid them.</div>
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="ar-pay-card">
        <div className="ar-pay-fields">
          <label>
            <span>Vendor</span>
            <select value={vendorId} onChange={(e) => chooseVendor(e.target.value)}>
              <option value="">Choose a vendor…</option>
              {owed.length > 0 && (
                <optgroup label="You owe">
                  {owed.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name} ({money(owing[String(v.vendor_id)])})</option>)}
                </optgroup>
              )}
              <optgroup label={owed.length ? 'Everyone else' : 'Vendors'}>
                {others.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
              </optgroup>
            </select>
          </label>
          <label>
            <span>Date paid</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            <span>Paid by</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option>Transfer</option>
              <option>Cash</option>
              <option>Check</option>
            </select>
          </label>
          {books && (
            <label>
              <span>Pay from</span>
              <DefaultAccountSelect use="out" method={method} value={payFrom} onChange={setPayFrom} />
            </label>
          )}
          <label>
            <span>Reference (optional)</span>
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Bank reference or cheque number" />
          </label>
        </div>
      </div>

      <div className="ar-pay-card">
        <div className="ar-pay-card-head">
          <div>
            <h2>Bills to pay</h2>
            <p>{selected ? <>Unpaid bills from <Link to={`${viaAccounting ? '/accounting' : '/purchase'}/ap/vendor/${selected.vendor_id}`}>{selected.name}</Link>.</> : 'Choose a vendor to see what you owe them.'}</p>
          </div>
          {rows.length > 0 && (
            totalPay > 0
              ? <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, pay: '' })))}>Clear amounts</button>
              : <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, pay: r.balance.toFixed(2) })))}>Pay everything</button>
          )}
        </div>
        <div className="ar-pay-table-wrap">
          <table className="ar-pay-table">
            <thead>
              <tr>
                <th>Bill</th>
                <th>Date</th>
                <th>Due</th>
                <th className="num">Total</th>
                <th className="num">Still owed</th>
                <th className="num">Paying now</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><div className="ci-empty">Loading…</div></td></tr>
              ) : !vendorId ? (
                <tr><td colSpan="7"><div className="ci-empty">Choose a vendor above.</div></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">You don’t owe this vendor anything.</div></td></tr>
              ) : rows.map((r, i) => {
                const due = parseWallClock(r.due_date)
                const overdue = due && due < today
                return (
                  <tr key={r.invoice_id}>
                    <td>{r.invoice_number || docNo('bill', r.bill_no)}</td>
                    <td>{fmtDay(r.invoice_date) || '–'}</td>
                    <td className={overdue ? 'ci-overdue-cell' : undefined}>{fmtDay(r.due_date) || '–'}{overdue ? ' · overdue' : ''}</td>
                    <td className="num">{money(r.total_amount)}</td>
                    <td className="num ar-pay-balance">{money(r.balance)}</td>
                    <td className="num">
                      <input className="ar-pay-amount" value={r.pay} inputMode="decimal" placeholder="0.00" onChange={(e) => setRowPay(i, e.target.value)} aria-label={`Paying now on ${r.invoice_number || r.invoice_id}`} />
                    </td>
                    <td><button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setRowPay(i, r.balance.toFixed(2))}>Full amount</button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ar-pay-footer">
          <div className="ar-pay-total">
            <span>Total paid</span>
            <strong>{money(totalPay)}</strong>
          </div>
          <button data-write type="button" className="ci-btn ci-btn-primary" onClick={onSave} disabled={saving || totalPay <= 0}>
            <Check size={14} />
            {saving ? 'Saving…' : 'Save payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
